import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  ReceiptLong as ReceivableIcon,
  RequestQuote as ContractIcon,
  Savings as CashIcon,
  TrendingDown as ExpenseIcon,
  WarningAmber as AlertIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import adminService from '../../services/AdminService';
import {
  AdminBankAccount,
  AdminFinancialOverview,
  AdminOwnerContract,
  AdminOwnerErpDetail,
  AdminOwnerOption,
  AdminOwnerSummary,
  AdminPayable,
  AdminReceivable,
} from '../../types/admin/AdminTypes';
import MetricCard from './MetricCard';

interface AdminErpWorkspaceProps {
  initialFinancialOverview?: AdminFinancialOverview | null;
  onDataChanged?: () => void | Promise<void>;
}

interface ContractFormState {
  ownerId: string;
  title: string;
  contractReference: string;
  status: string;
  manualBillingMode: string;
  billingMethod: string;
  startDate: string;
  endDate: string;
  firstDueDate: string;
  installmentCount: string;
  totalAmount: string;
  notes: string;
  regenerateReceivables: boolean;
}

interface ReceivableFormState {
  ownerId: string;
  contractId: string;
  description: string;
  installmentNumber: string;
  dueDate: string;
  amount: string;
  status: string;
  paymentMethod: string;
  bankAccountId: string;
  notes: string;
}

interface ReceiveFormState {
  receivedAmount: string;
  receivedAt: string;
  paymentMethod: string;
  bankAccountId: string;
  notes: string;
}

interface PayableFormState {
  description: string;
  expenseCategory: string;
  dueDate: string;
  amount: string;
  status: string;
  paymentMethod: string;
  bankAccountId: string;
  notes: string;
}

interface PayFormState {
  paidAmount: string;
  paidAt: string;
  paymentMethod: string;
  bankAccountId: string;
  notes: string;
}

interface BankAccountFormState {
  name: string;
  bankName: string;
  agency: string;
  accountNumber: string;
  initialBalance: string;
  active: boolean;
  notes: string;
}

interface TabPanelProps {
  readonly value: number;
  readonly index: number;
  readonly children: React.ReactNode;
}

const defaultContractForm: ContractFormState = {
  ownerId: '',
  title: '',
  contractReference: '',
  status: 'ativo',
  manualBillingMode: 'parcelado_12',
  billingMethod: 'boleto',
  startDate: '',
  endDate: '',
  firstDueDate: '',
  installmentCount: '12',
  totalAmount: '',
  notes: '',
  regenerateReceivables: true
};

const defaultReceivableForm: ReceivableFormState = {
  ownerId: '',
  contractId: '',
  description: '',
  installmentNumber: '',
  dueDate: '',
  amount: '',
  status: 'pendente',
  paymentMethod: '',
  bankAccountId: '',
  notes: ''
};

const defaultReceiveForm: ReceiveFormState = {
  receivedAmount: '',
  receivedAt: '',
  paymentMethod: '',
  bankAccountId: '',
  notes: ''
};

const defaultPayableForm: PayableFormState = {
  description: '',
  expenseCategory: '',
  dueDate: '',
  amount: '',
  status: 'pendente',
  paymentMethod: '',
  bankAccountId: '',
  notes: ''
};

const defaultPayForm: PayFormState = {
  paidAmount: '',
  paidAt: '',
  paymentMethod: '',
  bankAccountId: '',
  notes: ''
};

const defaultBankForm: BankAccountFormState = {
  name: '',
  bankName: '',
  agency: '',
  accountNumber: '',
  initialBalance: '0',
  active: true,
  notes: ''
};

const contractStatusOptions = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'encerrado', label: 'Encerrado' },
  { value: 'cancelado', label: 'Cancelado' }
];

const billingCategoryOptions = [
  { value: 'gratuito_real', label: 'Gratuito real' },
  { value: 'pago_site', label: 'Pago pelo site' },
  { value: 'pago_manual', label: 'Pago manual' }
];

const manualBillingModeOptions = [
  { value: 'anual_quitado', label: 'Anual quitado' },
  { value: 'parcelado_12', label: 'Parcelado em 12x' },
  { value: 'outro_manual', label: 'Outro manual' }
];

const receivableStatusOptions = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'atrasado', label: 'Atrasado' },
  { value: 'cancelado', label: 'Cancelado' }
];

const payableStatusOptions = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'atrasado', label: 'Atrasado' },
  { value: 'cancelado', label: 'Cancelado' }
];

const bankChipColor = (status: string) => {
  switch (status) {
    case 'vencido':
    case 'cancelado':
    case 'atrasado':
      return 'error';
    case 'a_vencer':
    case 'parcial':
      return 'warning';
    case 'vigente':
    case 'ativo':
    case 'recebido':
    case 'pago':
      return 'success';
    default:
      return 'default';
  }
};

const formatCurrency = (value?: number | null) => {
  const normalizedValue = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(normalizedValue);
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString('pt-BR');
};

const toDateTimeLocalValue = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  const pad = (number: number) => number.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toLocalDateTimePayload = (value?: string) => {
  if (!value) {
    return undefined;
  }

  return value.length === 16 ? `${value}:00` : value;
};

const mergeOwnerOptions = (selectedOwner: AdminOwnerOption | null, owners: AdminOwnerSummary[]) => {
  const options = selectedOwner ? [selectedOwner, ...owners.map((item) => item.owner)] : owners.map((item) => item.owner);
  const unique = new Map<string, AdminOwnerOption>();
  options.forEach((option) => unique.set(option.id, option));
  return Array.from(unique.values());
};

function TabPanel({ value, index, children }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SectionPaper: React.FC<{ title: string; subtitle?: string; actions?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  subtitle,
  actions,
  children
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: '1px solid #e0e0e0'
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions}
    </Box>
    {children}
  </Paper>
);

const AdminErpWorkspace: React.FC<AdminErpWorkspaceProps> = ({ initialFinancialOverview, onDataChanged }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [financialOverview, setFinancialOverview] = useState<AdminFinancialOverview | null>(initialFinancialOverview ?? null);
  const [summaryStartDate, setSummaryStartDate] = useState('');
  const [summaryEndDate, setSummaryEndDate] = useState('');

  const [owners, setOwners] = useState<AdminOwnerSummary[]>([]);
  const [ownersTotal, setOwnersTotal] = useState(0);
  const [ownersPage, setOwnersPage] = useState(1);
  const [ownersRowsPerPage, setOwnersRowsPerPage] = useState(10);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>('');
  const [ownerDetail, setOwnerDetail] = useState<AdminOwnerErpDetail | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState({
    billingCategory: 'gratuito_real',
    manualBillingMode: '',
    notes: ''
  });

  const [contracts, setContracts] = useState<AdminOwnerContract[]>([]);
  const [contractFilters, setContractFilters] = useState({
    ownerId: '',
    status: '',
    search: ''
  });

  const [receivables, setReceivables] = useState<AdminReceivable[]>([]);
  const [receivableFilters, setReceivableFilters] = useState({
    ownerId: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  const [payables, setPayables] = useState<AdminPayable[]>([]);
  const [payableFilters, setPayableFilters] = useState({
    status: '',
    startDate: '',
    endDate: ''
  });

  const [bankAccounts, setBankAccounts] = useState<AdminBankAccount[]>([]);

  const [ownerLookupSearch, setOwnerLookupSearch] = useState('');
  const [ownerLookupOptions, setOwnerLookupOptions] = useState<AdminOwnerSummary[]>([]);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<AdminOwnerContract | null>(null);
  const [contractForm, setContractForm] = useState<ContractFormState>(defaultContractForm);
  const [selectedContractOwner, setSelectedContractOwner] = useState<AdminOwnerOption | null>(null);

  const [receivableDialogOpen, setReceivableDialogOpen] = useState(false);
  const [editingReceivable, setEditingReceivable] = useState<AdminReceivable | null>(null);
  const [receivableForm, setReceivableForm] = useState<ReceivableFormState>(defaultReceivableForm);
  const [selectedReceivableOwner, setSelectedReceivableOwner] = useState<AdminOwnerOption | null>(null);
  const [receivableContractOptions, setReceivableContractOptions] = useState<AdminOwnerContract[]>([]);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receivableBeingReceived, setReceivableBeingReceived] = useState<AdminReceivable | null>(null);
  const [receiveForm, setReceiveForm] = useState<ReceiveFormState>(defaultReceiveForm);

  const [payableDialogOpen, setPayableDialogOpen] = useState(false);
  const [editingPayable, setEditingPayable] = useState<AdminPayable | null>(null);
  const [payableForm, setPayableForm] = useState<PayableFormState>(defaultPayableForm);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payableBeingPaid, setPayableBeingPaid] = useState<AdminPayable | null>(null);
  const [payForm, setPayForm] = useState<PayFormState>(defaultPayForm);

  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState<AdminBankAccount | null>(null);
  const [bankForm, setBankForm] = useState<BankAccountFormState>(defaultBankForm);

  const [savingDialog, setSavingDialog] = useState(false);

  useEffect(() => {
    if (initialFinancialOverview) {
      setFinancialOverview(initialFinancialOverview);
    }
  }, [initialFinancialOverview]);

  useEffect(() => {
    void bootstrap();
  }, []);

  useEffect(() => {
    void loadOwners();
  }, [ownersPage, ownersRowsPerPage, ownerSearch]);

  useEffect(() => {
    if (selectedOwnerId) {
      void loadOwnerDetail(selectedOwnerId);
    }
  }, [selectedOwnerId]);

  useEffect(() => {
    void loadContracts();
  }, [contractFilters.ownerId, contractFilters.status, contractFilters.search]);

  useEffect(() => {
    void loadReceivables();
  }, [receivableFilters.ownerId, receivableFilters.status, receivableFilters.startDate, receivableFilters.endDate]);

  useEffect(() => {
    void loadPayables();
  }, [payableFilters.status, payableFilters.startDate, payableFilters.endDate]);

  useEffect(() => {
    const shouldLoadOwners = contractDialogOpen || receivableDialogOpen;
    if (!shouldLoadOwners) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadOwnerLookupOptions(ownerLookupSearch);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [ownerLookupSearch, contractDialogOpen, receivableDialogOpen]);

  useEffect(() => {
    if (!receivableDialogOpen || !receivableForm.ownerId) {
      setReceivableContractOptions([]);
      return;
    }

    void loadReceivableContractOptions(receivableForm.ownerId);
  }, [receivableDialogOpen, receivableForm.ownerId]);

  const contractOwnerOptions = useMemo(
    () => mergeOwnerOptions(selectedContractOwner, ownerLookupOptions),
    [selectedContractOwner, ownerLookupOptions]
  );

  const receivableOwnerOptions = useMemo(
    () => mergeOwnerOptions(selectedReceivableOwner, ownerLookupOptions),
    [selectedReceivableOwner, ownerLookupOptions]
  );

  const filteredContractsForReceivableOwner = useMemo(() => {
    if (!receivableForm.ownerId) {
      return receivableContractOptions;
    }
    return receivableContractOptions.filter((contract) => contract.ownerId === receivableForm.ownerId);
  }, [receivableContractOptions, receivableForm.ownerId]);

  const bootstrap = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadFinancialOverview(),
        loadOwners(),
        loadContracts(),
        loadReceivables(),
        loadPayables(),
        loadBankAccounts()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const notifyParent = async () => {
    if (onDataChanged) {
      await onDataChanged();
    }
  };

  const refreshEverything = async () => {
    await Promise.all([
      loadFinancialOverview(summaryStartDate || undefined, summaryEndDate || undefined),
      loadOwners(),
      loadContracts(),
      loadReceivables(),
      loadPayables(),
      loadBankAccounts(),
      selectedOwnerId ? loadOwnerDetail(selectedOwnerId) : Promise.resolve(),
      notifyParent()
    ]);
  };

  const loadFinancialOverview = async (startDate?: string, endDate?: string) => {
    try {
      const response = await adminService.getFinancialSummary(startDate, endDate);
      setFinancialOverview(response);
    } catch (error) {
      console.error('Erro ao carregar resumo financeiro:', error);
      toast.error('Erro ao carregar resumo financeiro');
    }
  };

  const loadOwners = async () => {
    try {
      const response = await adminService.getOwners(ownersPage, ownersRowsPerPage, ownerSearch || undefined);
      setOwners(response.owners);
      setOwnersTotal(response.total);

      if (!selectedOwnerId && response.owners.length > 0) {
        setSelectedOwnerId(response.owners[0].owner.id);
      }
    } catch (error) {
      console.error('Erro ao carregar owners:', error);
      toast.error('Erro ao carregar owners');
    }
  };

  const loadOwnerLookupOptions = async (search?: string) => {
    try {
      const response = await adminService.getOwners(1, 100, search || undefined);
      setOwnerLookupOptions(response.owners);
    } catch (error) {
      console.error('Erro ao carregar opções de owner:', error);
    }
  };

  const loadReceivableContractOptions = async (ownerId: string) => {
    try {
      const response = await adminService.getContracts({ ownerId });
      setReceivableContractOptions(response);
    } catch (error) {
      console.error('Erro ao carregar contratos do owner para recebível:', error);
    }
  };

  const loadOwnerDetail = async (ownerId: string) => {
    try {
      const response = await adminService.getOwnerDetail(ownerId);
      setOwnerDetail(response);
      setProfileDraft({
        billingCategory: response.profile.billingCategory,
        manualBillingMode: response.profile.manualBillingMode ?? '',
        notes: response.profile.notes ?? ''
      });
    } catch (error) {
      console.error('Erro ao carregar detalhe do owner:', error);
      toast.error('Erro ao carregar detalhe do owner');
    }
  };

  const loadContracts = async () => {
    try {
      const response = await adminService.getContracts({
        ownerId: contractFilters.ownerId || undefined,
        status: contractFilters.status || undefined,
        search: contractFilters.search || undefined
      });
      setContracts(response);
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
      toast.error('Erro ao carregar contratos');
    }
  };

  const loadReceivables = async () => {
    try {
      const response = await adminService.getReceivables({
        ownerId: receivableFilters.ownerId || undefined,
        status: receivableFilters.status || undefined,
        startDate: receivableFilters.startDate || undefined,
        endDate: receivableFilters.endDate || undefined
      });
      setReceivables(response);
    } catch (error) {
      console.error('Erro ao carregar contas a receber:', error);
      toast.error('Erro ao carregar contas a receber');
    }
  };

  const loadPayables = async () => {
    try {
      const response = await adminService.getPayables({
        status: payableFilters.status || undefined,
        startDate: payableFilters.startDate || undefined,
        endDate: payableFilters.endDate || undefined
      });
      setPayables(response);
    } catch (error) {
      console.error('Erro ao carregar contas a pagar:', error);
      toast.error('Erro ao carregar contas a pagar');
    }
  };

  const loadBankAccounts = async () => {
    try {
      const response = await adminService.getBankAccounts();
      setBankAccounts(response);
    } catch (error) {
      console.error('Erro ao carregar contas bancárias:', error);
      toast.error('Erro ao carregar contas bancárias');
    }
  };

  const handleSaveProfile = async () => {
    if (!selectedOwnerId) {
      return;
    }

    try {
      setSavingProfile(true);
      await adminService.updateOwnerProfile(selectedOwnerId, {
        billingCategory: profileDraft.billingCategory,
        manualBillingMode: profileDraft.billingCategory === 'pago_manual' ? profileDraft.manualBillingMode : undefined,
        notes: profileDraft.notes || undefined
      });

      toast.success('Perfil comercial atualizado');
      await Promise.all([loadOwnerDetail(selectedOwnerId), loadOwners(), notifyParent()]);
    } catch (error) {
      console.error('Erro ao atualizar perfil comercial:', error);
      toast.error('Erro ao atualizar perfil comercial');
    } finally {
      setSavingProfile(false);
    }
  };

  const openCreateContractDialog = () => {
    const preferredOwner = ownerDetail?.owner ?? owners.find((item) => item.owner.id === selectedOwnerId)?.owner ?? null;
    setEditingContract(null);
    setContractForm({
      ...defaultContractForm,
      ownerId: preferredOwner?.id ?? ''
    });
    setSelectedContractOwner(preferredOwner);
    setOwnerLookupSearch('');
    setContractDialogOpen(true);
  };

  const openEditContractDialog = (contract: AdminOwnerContract) => {
    setEditingContract(contract);
    setContractForm({
      ownerId: contract.ownerId,
      title: contract.title,
      contractReference: contract.contractReference ?? '',
      status: contract.status,
      manualBillingMode: contract.manualBillingMode,
      billingMethod: contract.billingMethod,
      startDate: contract.startDate,
      endDate: contract.endDate,
      firstDueDate: contract.firstDueDate,
      installmentCount: String(contract.installmentCount),
      totalAmount: String(contract.totalAmount),
      notes: contract.notes ?? '',
      regenerateReceivables: false
    });
    setSelectedContractOwner({
      id: contract.ownerId,
      name: contract.ownerName ?? 'Owner',
      email: '',
      companyGuid: contract.companyGuid,
      companyName: contract.companyName
    });
    setContractDialogOpen(true);
  };

  const closeContractDialog = () => {
    setContractDialogOpen(false);
    setEditingContract(null);
    setContractForm(defaultContractForm);
    setSelectedContractOwner(null);
    setOwnerLookupOptions([]);
    setOwnerLookupSearch('');
  };

  const handleSaveContract = async () => {
    if (!contractForm.ownerId || !contractForm.title.trim() || !contractForm.totalAmount || !contractForm.startDate || !contractForm.endDate || !contractForm.firstDueDate) {
      toast.warning('Preencha os campos obrigatórios do contrato');
      return;
    }

    try {
      setSavingDialog(true);
      const payload = {
        ownerId: contractForm.ownerId,
        title: contractForm.title.trim(),
        contractReference: contractForm.contractReference.trim() || undefined,
        status: contractForm.status,
        manualBillingMode: contractForm.manualBillingMode,
        billingMethod: contractForm.billingMethod.trim(),
        startDate: contractForm.startDate,
        endDate: contractForm.endDate,
        firstDueDate: contractForm.firstDueDate,
        installmentCount: Number(contractForm.installmentCount || 1),
        totalAmount: Number(contractForm.totalAmount || 0),
        notes: contractForm.notes.trim() || undefined,
        regenerateReceivables: contractForm.regenerateReceivables
      };

      if (editingContract) {
        await adminService.updateContract(editingContract.id, payload);
        toast.success('Contrato atualizado');
      } else {
        await adminService.createContract(payload);
        toast.success('Contrato criado');
      }

      closeContractDialog();
      await refreshEverything();
    } catch (error) {
      console.error('Erro ao salvar contrato:', error);
      toast.error('Erro ao salvar contrato');
    } finally {
      setSavingDialog(false);
    }
  };

  const openCreateReceivableDialog = () => {
    const preferredOwner = ownerDetail?.owner ?? owners.find((item) => item.owner.id === selectedOwnerId)?.owner ?? null;
    const currentContract = ownerDetail?.currentContract ?? null;
    setEditingReceivable(null);
    setReceivableForm({
      ...defaultReceivableForm,
      ownerId: preferredOwner?.id ?? '',
      contractId: currentContract && currentContract.ownerId === preferredOwner?.id ? currentContract.id : ''
    });
    setSelectedReceivableOwner(preferredOwner);
    setReceivableContractOptions(ownerDetail?.contracts ?? []);
    setOwnerLookupSearch('');
    setReceivableDialogOpen(true);
  };

  const openEditReceivableDialog = (receivable: AdminReceivable) => {
    setEditingReceivable(receivable);
    setReceivableForm({
      ownerId: receivable.ownerId,
      contractId: receivable.contractId ?? '',
      description: receivable.description,
      installmentNumber: receivable.installmentNumber ? String(receivable.installmentNumber) : '',
      dueDate: receivable.dueDate,
      amount: String(receivable.amount),
      status: receivable.status,
      paymentMethod: receivable.paymentMethod ?? '',
      bankAccountId: receivable.bankAccountId ?? '',
      notes: receivable.notes ?? ''
    });
    setSelectedReceivableOwner({
      id: receivable.ownerId,
      name: receivable.ownerName ?? 'Owner',
      email: '',
      companyGuid: '',
      companyName: receivable.companyName
    });
    setReceivableDialogOpen(true);
  };

  const closeReceivableDialog = () => {
    setReceivableDialogOpen(false);
    setEditingReceivable(null);
    setReceivableForm(defaultReceivableForm);
    setSelectedReceivableOwner(null);
    setReceivableContractOptions([]);
    setOwnerLookupOptions([]);
    setOwnerLookupSearch('');
  };

  const handleSaveReceivable = async () => {
    if (!receivableForm.ownerId || !receivableForm.description.trim() || !receivableForm.dueDate || !receivableForm.amount) {
      toast.warning('Preencha os campos obrigatórios da conta a receber');
      return;
    }

    try {
      setSavingDialog(true);
      const payload = {
        ownerId: receivableForm.ownerId,
        contractId: receivableForm.contractId || undefined,
        description: receivableForm.description.trim(),
        installmentNumber: receivableForm.installmentNumber ? Number(receivableForm.installmentNumber) : undefined,
        dueDate: receivableForm.dueDate,
        amount: Number(receivableForm.amount || 0),
        status: receivableForm.status,
        paymentMethod: receivableForm.paymentMethod || undefined,
        bankAccountId: receivableForm.bankAccountId || undefined,
        notes: receivableForm.notes.trim() || undefined
      };

      if (editingReceivable) {
        await adminService.updateReceivable(editingReceivable.id, payload);
        toast.success('Conta a receber atualizada');
      } else {
        await adminService.createReceivable(payload);
        toast.success('Conta a receber criada');
      }

      closeReceivableDialog();
      await refreshEverything();
    } catch (error) {
      console.error('Erro ao salvar conta a receber:', error);
      toast.error('Erro ao salvar conta a receber');
    } finally {
      setSavingDialog(false);
    }
  };

  const openReceiveDialog = (receivable: AdminReceivable) => {
    setReceivableBeingReceived(receivable);
    setReceiveForm({
      receivedAmount: String((receivable.amount ?? 0) - (receivable.receivedAmount ?? 0)),
      receivedAt: toDateTimeLocalValue(new Date().toISOString()),
      paymentMethod: receivable.paymentMethod ?? '',
      bankAccountId: receivable.bankAccountId ?? '',
      notes: receivable.notes ?? ''
    });
    setReceiveDialogOpen(true);
  };

  const closeReceiveDialog = () => {
    setReceiveDialogOpen(false);
    setReceivableBeingReceived(null);
    setReceiveForm(defaultReceiveForm);
  };

  const handleReceiveReceivable = async () => {
    if (!receivableBeingReceived || !receiveForm.receivedAmount) {
      toast.warning('Informe o valor recebido');
      return;
    }

    try {
      setSavingDialog(true);
      await adminService.receiveReceivable(receivableBeingReceived.id, {
        receivedAmount: Number(receiveForm.receivedAmount || 0),
        receivedAt: toLocalDateTimePayload(receiveForm.receivedAt),
        paymentMethod: receiveForm.paymentMethod || undefined,
        bankAccountId: receiveForm.bankAccountId || undefined,
        notes: receiveForm.notes.trim() || undefined
      });

      toast.success('Baixa financeira registrada');
      closeReceiveDialog();
      await refreshEverything();
    } catch (error) {
      console.error('Erro ao receber conta:', error);
      toast.error('Erro ao registrar recebimento');
    } finally {
      setSavingDialog(false);
    }
  };

  const openCreatePayableDialog = () => {
    setEditingPayable(null);
    setPayableForm(defaultPayableForm);
    setPayableDialogOpen(true);
  };

  const openEditPayableDialog = (payable: AdminPayable) => {
    setEditingPayable(payable);
    setPayableForm({
      description: payable.description,
      expenseCategory: payable.expenseCategory ?? '',
      dueDate: payable.dueDate,
      amount: String(payable.amount),
      status: payable.status,
      paymentMethod: payable.paymentMethod ?? '',
      bankAccountId: payable.bankAccountId ?? '',
      notes: payable.notes ?? ''
    });
    setPayableDialogOpen(true);
  };

  const closePayableDialog = () => {
    setPayableDialogOpen(false);
    setEditingPayable(null);
    setPayableForm(defaultPayableForm);
  };

  const handleSavePayable = async () => {
    if (!payableForm.description.trim() || !payableForm.dueDate || !payableForm.amount) {
      toast.warning('Preencha os campos obrigatórios da despesa');
      return;
    }

    try {
      setSavingDialog(true);
      const payload = {
        description: payableForm.description.trim(),
        expenseCategory: payableForm.expenseCategory.trim() || undefined,
        dueDate: payableForm.dueDate,
        amount: Number(payableForm.amount || 0),
        status: payableForm.status,
        paymentMethod: payableForm.paymentMethod || undefined,
        bankAccountId: payableForm.bankAccountId || undefined,
        notes: payableForm.notes.trim() || undefined
      };

      if (editingPayable) {
        await adminService.updatePayable(editingPayable.id, payload);
        toast.success('Despesa atualizada');
      } else {
        await adminService.createPayable(payload);
        toast.success('Despesa criada');
      }

      closePayableDialog();
      await refreshEverything();
    } catch (error) {
      console.error('Erro ao salvar despesa:', error);
      toast.error('Erro ao salvar despesa');
    } finally {
      setSavingDialog(false);
    }
  };

  const openPayDialog = (payable: AdminPayable) => {
    setPayableBeingPaid(payable);
    setPayForm({
      paidAmount: String((payable.amount ?? 0) - (payable.paidAmount ?? 0)),
      paidAt: toDateTimeLocalValue(new Date().toISOString()),
      paymentMethod: payable.paymentMethod ?? '',
      bankAccountId: payable.bankAccountId ?? '',
      notes: payable.notes ?? ''
    });
    setPayDialogOpen(true);
  };

  const closePayDialog = () => {
    setPayDialogOpen(false);
    setPayableBeingPaid(null);
    setPayForm(defaultPayForm);
  };

  const handlePayPayable = async () => {
    if (!payableBeingPaid || !payForm.paidAmount) {
      toast.warning('Informe o valor pago');
      return;
    }

    try {
      setSavingDialog(true);
      await adminService.payPayable(payableBeingPaid.id, {
        paidAmount: Number(payForm.paidAmount || 0),
        paidAt: toLocalDateTimePayload(payForm.paidAt),
        paymentMethod: payForm.paymentMethod || undefined,
        bankAccountId: payForm.bankAccountId || undefined,
        notes: payForm.notes.trim() || undefined
      });

      toast.success('Pagamento registrado');
      closePayDialog();
      await refreshEverything();
    } catch (error) {
      console.error('Erro ao pagar despesa:', error);
      toast.error('Erro ao registrar pagamento');
    } finally {
      setSavingDialog(false);
    }
  };

  const openCreateBankDialog = () => {
    setEditingBankAccount(null);
    setBankForm(defaultBankForm);
    setBankDialogOpen(true);
  };

  const openEditBankDialog = (bankAccount: AdminBankAccount) => {
    setEditingBankAccount(bankAccount);
    setBankForm({
      name: bankAccount.name,
      bankName: bankAccount.bankName,
      agency: bankAccount.agency ?? '',
      accountNumber: bankAccount.accountNumber ?? '',
      initialBalance: String(bankAccount.initialBalance),
      active: bankAccount.active,
      notes: bankAccount.notes ?? ''
    });
    setBankDialogOpen(true);
  };

  const closeBankDialog = () => {
    setBankDialogOpen(false);
    setEditingBankAccount(null);
    setBankForm(defaultBankForm);
  };

  const handleSaveBankAccount = async () => {
    if (!bankForm.name.trim() || !bankForm.bankName.trim() || bankForm.initialBalance === '') {
      toast.warning('Preencha os campos obrigatórios da conta bancária');
      return;
    }

    try {
      setSavingDialog(true);
      const payload = {
        name: bankForm.name.trim(),
        bankName: bankForm.bankName.trim(),
        agency: bankForm.agency.trim() || undefined,
        accountNumber: bankForm.accountNumber.trim() || undefined,
        initialBalance: Number(bankForm.initialBalance || 0),
        active: bankForm.active,
        notes: bankForm.notes.trim() || undefined
      };

      if (editingBankAccount) {
        await adminService.updateBankAccount(editingBankAccount.id, payload);
        toast.success('Conta bancária atualizada');
      } else {
        await adminService.createBankAccount(payload);
        toast.success('Conta bancária criada');
      }

      closeBankDialog();
      await refreshEverything();
    } catch (error) {
      console.error('Erro ao salvar conta bancária:', error);
      toast.error('Erro ao salvar conta bancária');
    } finally {
      setSavingDialog(false);
    }
  };

  const renderStatusChip = (label: string, status: string) => (
    <Chip label={label} size="small" color={bankChipColor(status) as any} variant={status === 'vigente' || status === 'ativo' || status === 'recebido' || status === 'pago' ? 'filled' : 'outlined'} />
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            ERP Administrativo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contratos, vigências, recebimentos, despesas e caixa operacional do portal admin.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => void refreshEverything()}>
          Atualizar ERP
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} variant="scrollable" scrollButtons="auto">
          <Tab label="Resumo" />
          <Tab label="Owners" />
          <Tab label="Contratos" />
          <Tab label="Recebíveis" />
          <Tab label="Despesas" />
          <Tab label="Bancos" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Caixa Consolidado"
              value={formatCurrency(financialOverview?.cashBalance)}
              subtitle="Saldo manual consolidado"
              icon={<CashIcon sx={{ color: '#2e7d32' }} />}
              iconBgColor="#e8f5e9"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Receita no Período"
              value={formatCurrency(financialOverview?.totalRevenuePeriod)}
              subtitle={`Site ${formatCurrency(financialOverview?.siteRevenuePeriod)} + manual ${formatCurrency(financialOverview?.manualRevenuePeriod)}`}
              icon={<ReceivableIcon sx={{ color: '#0f6bff' }} />}
              iconBgColor="#eef4ff"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="A Receber em Aberto"
              value={formatCurrency(financialOverview?.openReceivables)}
              subtitle={`Em atraso: ${formatCurrency(financialOverview?.overdueReceivables)}`}
              icon={<ContractIcon sx={{ color: '#8a5b00' }} />}
              iconBgColor="#fff8e8"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Despesas no Período"
              value={formatCurrency(financialOverview?.expensesPeriod)}
              subtitle={`Em aberto: ${formatCurrency(financialOverview?.outstandingPayables)}`}
              icon={<ExpenseIcon sx={{ color: '#d64545' }} />}
              iconBgColor="#fff1f1"
            />
          </Grid>
        </Grid>

        <SectionPaper
          title="Filtro de Período"
          subtitle="Ajuste o resumo financeiro do ERP por intervalo."
          actions={(
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => {
                setSummaryStartDate('');
                setSummaryEndDate('');
                void loadFinancialOverview();
              }}>
                Limpar
              </Button>
              <Button variant="contained" onClick={() => void loadFinancialOverview(summaryStartDate || undefined, summaryEndDate || undefined)}>
                Aplicar
              </Button>
            </Stack>
          )}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Data inicial"
                type="date"
                value={summaryStartDate}
                onChange={(event) => setSummaryStartDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Data final"
                type="date"
                value={summaryEndDate}
                onChange={(event) => setSummaryEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </SectionPaper>

        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionPaper
              title="Saldos por Conta Bancária"
              subtitle={`Recebimentos de gateway sem vinculação bancária: ${formatCurrency(financialOverview?.unallocatedGatewayReceipts)}`}
            >
              <Stack spacing={1.5}>
                {financialOverview?.bankBalances?.length ? (
                  financialOverview.bankBalances.map((bank) => (
                    <Box
                      key={bank.bankAccountId}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{bank.bankAccountName}</Typography>
                        <Typography variant="body2" color="text.secondary">{bank.bankName}</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700, color: bank.currentBalance >= 0 ? '#2e7d32' : '#d64545' }}>
                        {formatCurrency(bank.currentBalance)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Alert severity="info">Nenhuma conta bancária cadastrada ainda.</Alert>
                )}
              </Stack>
            </SectionPaper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionPaper
              title="Alertas de Contrato"
              subtitle={`${financialOverview?.contractsExpiringSoon ?? 0} a vencer e ${financialOverview?.overdueContracts ?? 0} vencidos`}
            >
              <Stack spacing={1.5}>
                {financialOverview?.contractAlerts?.length ? (
                  financialOverview.contractAlerts.map((alert) => (
                    <Alert
                      key={alert.contractId}
                      severity={alert.alertType === 'vencido' ? 'error' : 'warning'}
                      icon={<AlertIcon fontSize="inherit" />}
                    >
                      <Typography sx={{ fontWeight: 600 }}>{alert.title}</Typography>
                      <Typography variant="body2">
                        {alert.ownerName} • {alert.companyName} • vigência até {formatDate(alert.endDate)}
                      </Typography>
                      <Typography variant="body2">{alert.alertMessage}</Typography>
                    </Alert>
                  ))
                ) : (
                  <Alert severity="success">Nenhum alerta de contrato no período atual.</Alert>
                )}
              </Stack>
            </SectionPaper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 5 }}>
            <SectionPaper title="Owners" subtitle="Selecione um owner para ver o detalhe ERP.">
              <TextField
                fullWidth
                size="small"
                label="Buscar owner"
                value={ownerSearch}
                onChange={(event) => {
                  setOwnersPage(1);
                  setOwnerSearch(event.target.value);
                }}
                sx={{ mb: 2 }}
              />

              <TableContainer sx={{ maxHeight: 460 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Owner</TableCell>
                      <TableCell>Categoria</TableCell>
                      <TableCell>Receita Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {owners.map((item) => (
                      <TableRow
                        key={item.owner.id}
                        hover
                        selected={selectedOwnerId === item.owner.id}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedOwnerId(item.owner.id)}
                      >
                        <TableCell>
                          <Typography sx={{ fontWeight: 600 }}>{item.owner.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{item.owner.companyName || item.owner.email}</Typography>
                        </TableCell>
                        <TableCell>{renderStatusChip(item.profile.billingCategoryLabel, item.profile.billingCategory)}</TableCell>
                        <TableCell>{formatCurrency(item.summary.totalReceived)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={ownersTotal}
                page={Math.max(ownersPage - 1, 0)}
                onPageChange={(_, page) => setOwnersPage(page + 1)}
                rowsPerPage={ownersRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setOwnersRowsPerPage(Number(event.target.value));
                  setOwnersPage(1);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Linhas por página"
              />
            </SectionPaper>
          </Grid>

          <Grid size={{ xs: 12, lg: 7 }}>
            {ownerDetail ? (
              <Stack spacing={3}>
                <SectionPaper
                  title={ownerDetail.owner.name}
                  subtitle={`${ownerDetail.owner.companyName ?? 'Sem empresa'} • ${ownerDetail.owner.email}`}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <MetricCard
                        title="Recebido Manual"
                        value={formatCurrency(ownerDetail.summary.manualReceivedTotal)}
                        subtitle="Lançamentos ERP"
                        icon={<ReceivableIcon sx={{ color: '#0f6bff' }} />}
                        iconBgColor="#eef4ff"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <MetricCard
                        title="Recebido Site"
                        value={formatCurrency(ownerDetail.summary.siteReceivedTotal)}
                        subtitle="Gateway/site"
                        icon={<PaymentIcon sx={{ color: '#2e7d32' }} />}
                        iconBgColor="#e8f5e9"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <MetricCard
                        title="Em Aberto"
                        value={formatCurrency(ownerDetail.summary.openReceivables)}
                        subtitle={`Em atraso ${formatCurrency(ownerDetail.summary.overdueReceivables)}`}
                        icon={<ContractIcon sx={{ color: '#8a5b00' }} />}
                        iconBgColor="#fff8e8"
                      />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper
                  title="Perfil Comercial"
                  subtitle="O ERP informa a situação comercial; a liberação de acesso continua no fluxo atual."
                  actions={(
                    <Button variant="contained" onClick={() => void handleSaveProfile()} disabled={savingProfile}>
                      {savingProfile ? 'Salvando...' : 'Salvar perfil'}
                    </Button>
                  )}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        select
                        fullWidth
                        label="Categoria"
                        value={profileDraft.billingCategory}
                        onChange={(event) => setProfileDraft((current) => ({ ...current, billingCategory: event.target.value }))}
                      >
                        {billingCategoryOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        select
                        fullWidth
                        label="Modalidade manual"
                        value={profileDraft.manualBillingMode}
                        onChange={(event) => setProfileDraft((current) => ({ ...current, manualBillingMode: event.target.value }))}
                        disabled={profileDraft.billingCategory !== 'pago_manual'}
                      >
                        {manualBillingModeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Observações"
                        multiline
                        rows={4}
                        value={profileDraft.notes}
                        onChange={(event) => setProfileDraft((current) => ({ ...current, notes: event.target.value }))}
                      />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper
                  title="Contrato Atual"
                  subtitle="Resumo da vigência mais relevante do owner."
                  actions={ownerDetail.currentContract ? (
                    <Button variant="outlined" onClick={() => openEditContractDialog(ownerDetail.currentContract!)}>
                      Editar contrato
                    </Button>
                  ) : (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateContractDialog}>
                      Novo contrato
                    </Button>
                  )}
                >
                  {ownerDetail.currentContract ? (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">Título</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{ownerDetail.currentContract.title}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        {renderStatusChip(ownerDetail.currentContract.statusLabel, ownerDetail.currentContract.status)}
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">Vigência</Typography>
                        {renderStatusChip(ownerDetail.currentContract.vigencyStatus.replace('_', ' '), ownerDetail.currentContract.vigencyStatus)}
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">Início</Typography>
                        <Typography>{formatDate(ownerDetail.currentContract.startDate)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">Fim</Typography>
                        <Typography>{formatDate(ownerDetail.currentContract.endDate)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">Valor total</Typography>
                        <Typography sx={{ fontWeight: 700 }}>{formatCurrency(ownerDetail.currentContract.totalAmount)}</Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Alert severity="info">Nenhum contrato ativo para este owner.</Alert>
                  )}
                </SectionPaper>

                <SectionPaper title="Últimos Recebíveis" subtitle="Prévia operacional do owner selecionado.">
                  <TableContainer sx={{ maxHeight: 260 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Descrição</TableCell>
                          <TableCell>Vencimento</TableCell>
                          <TableCell>Valor</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ownerDetail.receivables.length ? (
                          ownerDetail.receivables.slice(0, 6).map((receivable) => (
                            <TableRow key={receivable.id}>
                              <TableCell>{receivable.description}</TableCell>
                              <TableCell>{formatDate(receivable.dueDate)}</TableCell>
                              <TableCell>{formatCurrency(receivable.amount)}</TableCell>
                              <TableCell>{renderStatusChip(receivable.statusLabel, receivable.status)}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4}>Nenhum recebível cadastrado para este owner.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SectionPaper>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SectionPaper title="Histórico de Contratos" subtitle="Vigências registradas para o owner.">
                      <TableContainer sx={{ maxHeight: 260 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Contrato</TableCell>
                              <TableCell>Período</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ownerDetail.contracts.length ? (
                              ownerDetail.contracts.slice(0, 6).map((contract) => (
                                <TableRow key={contract.id}>
                                  <TableCell>
                                    <Typography sx={{ fontWeight: 600 }}>{contract.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {contract.manualBillingModeLabel}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{formatDate(contract.startDate)} - {formatDate(contract.endDate)}</TableCell>
                                  <TableCell>{renderStatusChip(contract.statusLabel, contract.status)}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3}>Nenhum contrato registrado para este owner.</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </SectionPaper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SectionPaper title="Pagamentos do Site" subtitle="Pagamentos vindos do fluxo atual da plataforma.">
                      <TableContainer sx={{ maxHeight: 260 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Plano</TableCell>
                              <TableCell>Valor</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ownerDetail.sitePayments.length ? (
                              ownerDetail.sitePayments.slice(0, 6).map((payment) => (
                                <TableRow key={payment.id}>
                                  <TableCell>{formatDateTime(payment.createdAt)}</TableCell>
                                  <TableCell>{payment.plan}</TableCell>
                                  <TableCell>{formatCurrency(payment.amount)}</TableCell>
                                  <TableCell>{renderStatusChip(payment.status, payment.status)}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4}>Nenhum pagamento de site encontrado para este owner.</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </SectionPaper>
                  </Grid>
                </Grid>
              </Stack>
            ) : (
              <SectionPaper title="Detalhe do owner">
                <Alert severity="info">Selecione um owner para visualizar o ERP detalhado.</Alert>
              </SectionPaper>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <SectionPaper
          title="Contratos"
          subtitle="Vigência, valor e modelo de cobrança por owner."
          actions={(
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateContractDialog}>
              Novo contrato
            </Button>
          )}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Buscar"
                value={contractFilters.search}
                onChange={(event) => setContractFilters((current) => ({ ...current, search: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={contractFilters.status}
                onChange={(event) => setContractFilters((current) => ({ ...current, status: event.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {contractStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Owner"
                value={contractFilters.ownerId}
                onChange={(event) => setContractFilters((current) => ({ ...current, ownerId: event.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {owners.map((item) => (
                  <MenuItem key={item.owner.id} value={item.owner.id}>
                    {item.owner.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contrato</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Vigência</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map((contract) => (
                  <TableRow key={contract.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{contract.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{contract.contractReference || '-'}</Typography>
                    </TableCell>
                    <TableCell>{contract.ownerName || '-'}</TableCell>
                    <TableCell>{formatDate(contract.startDate)} - {formatDate(contract.endDate)}</TableCell>
                    <TableCell>{contract.manualBillingModeLabel}</TableCell>
                    <TableCell>{formatCurrency(contract.totalAmount)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {renderStatusChip(contract.statusLabel, contract.status)}
                        {renderStatusChip(contract.vigencyStatus.replace('_', ' '), contract.vigencyStatus)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => openEditContractDialog(contract)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionPaper>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <SectionPaper
          title="Contas a Receber"
          subtitle="Recebíveis manuais e originados por contrato."
          actions={(
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateReceivableDialog}>
              Nova conta a receber
            </Button>
          )}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Owner"
                value={receivableFilters.ownerId}
                onChange={(event) => setReceivableFilters((current) => ({ ...current, ownerId: event.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {owners.map((item) => (
                  <MenuItem key={item.owner.id} value={item.owner.id}>
                    {item.owner.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={receivableFilters.status}
                onChange={(event) => setReceivableFilters((current) => ({ ...current, status: event.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {receivableStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="De"
                value={receivableFilters.startDate}
                onChange={(event) => setReceivableFilters((current) => ({ ...current, startDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Até"
                value={receivableFilters.endDate}
                onChange={(event) => setReceivableFilters((current) => ({ ...current, endDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Vencimento</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Recebido</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receivables.map((receivable) => (
                  <TableRow key={receivable.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{receivable.description}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {receivable.bankAccountName || 'Sem conta vinculada'}
                      </Typography>
                    </TableCell>
                    <TableCell>{receivable.ownerName || '-'}</TableCell>
                    <TableCell>{formatDate(receivable.dueDate)}</TableCell>
                    <TableCell>{formatCurrency(receivable.amount)}</TableCell>
                    <TableCell>{formatCurrency(receivable.receivedAmount)}</TableCell>
                    <TableCell>{renderStatusChip(receivable.statusLabel, receivable.status)}</TableCell>
                    <TableCell>{receivable.originLabel}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton onClick={() => openEditReceivableDialog(receivable)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {receivable.status !== 'recebido' && receivable.status !== 'cancelado' && (
                          <Button size="small" variant="outlined" onClick={() => openReceiveDialog(receivable)}>
                            Baixar
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionPaper>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <SectionPaper
          title="Contas a Pagar"
          subtitle="Despesas operacionais e pagamentos do caixa."
          actions={(
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreatePayableDialog}>
              Nova despesa
            </Button>
          )}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={payableFilters.status}
                onChange={(event) => setPayableFilters((current) => ({ ...current, status: event.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {payableStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="De"
                value={payableFilters.startDate}
                onChange={(event) => setPayableFilters((current) => ({ ...current, startDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Até"
                value={payableFilters.endDate}
                onChange={(event) => setPayableFilters((current) => ({ ...current, endDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Vencimento</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Pago</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Conta</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payables.map((payable) => (
                  <TableRow key={payable.id}>
                    <TableCell>{payable.description}</TableCell>
                    <TableCell>{payable.expenseCategory || '-'}</TableCell>
                    <TableCell>{formatDate(payable.dueDate)}</TableCell>
                    <TableCell>{formatCurrency(payable.amount)}</TableCell>
                    <TableCell>{formatCurrency(payable.paidAmount)}</TableCell>
                    <TableCell>{renderStatusChip(payable.statusLabel, payable.status)}</TableCell>
                    <TableCell>{payable.bankAccountName || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton onClick={() => openEditPayableDialog(payable)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {payable.status !== 'pago' && payable.status !== 'cancelado' && (
                          <Button size="small" variant="outlined" onClick={() => openPayDialog(payable)}>
                            Pagar
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionPaper>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <SectionPaper
          title="Contas Bancárias"
          subtitle="Cadastre bancos e acompanhe o saldo operacional manual."
          actions={(
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateBankDialog}>
              Nova conta
            </Button>
          )}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Conta</TableCell>
                  <TableCell>Banco</TableCell>
                  <TableCell>Agência</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell>Saldo inicial</TableCell>
                  <TableCell>Saldo atual</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bankAccounts.map((bankAccount) => (
                  <TableRow key={bankAccount.id}>
                    <TableCell>{bankAccount.name}</TableCell>
                    <TableCell>{bankAccount.bankName}</TableCell>
                    <TableCell>{bankAccount.agency || '-'}</TableCell>
                    <TableCell>{bankAccount.accountNumber || '-'}</TableCell>
                    <TableCell>{formatCurrency(bankAccount.initialBalance)}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: bankAccount.currentBalance >= 0 ? '#2e7d32' : '#d64545' }}>
                      {formatCurrency(bankAccount.currentBalance)}
                    </TableCell>
                    <TableCell>{renderStatusChip(bankAccount.active ? 'Ativa' : 'Inativa', bankAccount.active ? 'ativo' : 'cancelado')}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => openEditBankDialog(bankAccount)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionPaper>
      </TabPanel>

      <Dialog open={contractDialogOpen} onClose={closeContractDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingContract ? 'Editar contrato' : 'Novo contrato'}</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={contractOwnerOptions}
                value={selectedContractOwner}
                onChange={(_, value) => {
                  setSelectedContractOwner(value);
                  setContractForm((current) => ({ ...current, ownerId: value?.id ?? '' }));
                }}
                onInputChange={(_, value) => setOwnerLookupSearch(value)}
                getOptionLabel={(option) => `${option.name}${option.companyName ? ` • ${option.companyName}` : ''}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Owner" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Título"
                value={contractForm.title}
                onChange={(event) => setContractForm((current) => ({ ...current, title: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Referência"
                value={contractForm.contractReference}
                onChange={(event) => setContractForm((current) => ({ ...current, contractReference: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={contractForm.status}
                onChange={(event) => setContractForm((current) => ({ ...current, status: event.target.value }))}
              >
                {contractStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Modalidade manual"
                value={contractForm.manualBillingMode}
                onChange={(event) => setContractForm((current) => ({
                  ...current,
                  manualBillingMode: event.target.value,
                  installmentCount: event.target.value === 'anual_quitado' ? '1' : event.target.value === 'parcelado_12' ? '12' : current.installmentCount
                }))}
              >
                {manualBillingModeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Método de cobrança"
                value={contractForm.billingMethod}
                onChange={(event) => setContractForm((current) => ({ ...current, billingMethod: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Início da vigência"
                type="date"
                value={contractForm.startDate}
                onChange={(event) => setContractForm((current) => ({ ...current, startDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Fim da vigência"
                type="date"
                value={contractForm.endDate}
                onChange={(event) => setContractForm((current) => ({ ...current, endDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Primeiro vencimento"
                type="date"
                value={contractForm.firstDueDate}
                onChange={(event) => setContractForm((current) => ({ ...current, firstDueDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Parcelas"
                type="number"
                value={contractForm.installmentCount}
                onChange={(event) => setContractForm((current) => ({ ...current, installmentCount: event.target.value }))}
                disabled={contractForm.manualBillingMode === 'anual_quitado' || contractForm.manualBillingMode === 'parcelado_12'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Valor total"
                type="number"
                value={contractForm.totalAmount}
                onChange={(event) => setContractForm((current) => ({ ...current, totalAmount: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={contractForm.notes}
                onChange={(event) => setContractForm((current) => ({ ...current, notes: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={contractForm.regenerateReceivables}
                    onChange={(event) => setContractForm((current) => ({ ...current, regenerateReceivables: event.target.checked }))}
                  />
                )}
                label="Gerar/regenerar contas a receber do contrato"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeContractDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleSaveContract()} disabled={savingDialog}>
            {savingDialog ? 'Salvando...' : 'Salvar contrato'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={receivableDialogOpen} onClose={closeReceivableDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingReceivable ? 'Editar conta a receber' : 'Nova conta a receber'}</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={receivableOwnerOptions}
                value={selectedReceivableOwner}
                onChange={(_, value) => {
                  setSelectedReceivableOwner(value);
                  setReceivableForm((current) => ({ ...current, ownerId: value?.id ?? '', contractId: '' }));
                }}
                onInputChange={(_, value) => setOwnerLookupSearch(value)}
                getOptionLabel={(option) => `${option.name}${option.companyName ? ` • ${option.companyName}` : ''}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Owner" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Contrato"
                value={receivableForm.contractId}
                onChange={(event) => setReceivableForm((current) => ({ ...current, contractId: event.target.value }))}
              >
                <MenuItem value="">Sem contrato</MenuItem>
                {filteredContractsForReceivableOwner.map((contract) => (
                  <MenuItem key={contract.id} value={contract.id}>
                    {contract.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Descrição"
                value={receivableForm.description}
                onChange={(event) => setReceivableForm((current) => ({ ...current, description: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Parcela"
                type="number"
                value={receivableForm.installmentNumber}
                onChange={(event) => setReceivableForm((current) => ({ ...current, installmentNumber: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Vencimento"
                value={receivableForm.dueDate}
                onChange={(event) => setReceivableForm((current) => ({ ...current, dueDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="number"
                label="Valor"
                value={receivableForm.amount}
                onChange={(event) => setReceivableForm((current) => ({ ...current, amount: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={receivableForm.status}
                onChange={(event) => setReceivableForm((current) => ({ ...current, status: event.target.value }))}
              >
                {receivableStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Método"
                value={receivableForm.paymentMethod}
                onChange={(event) => setReceivableForm((current) => ({ ...current, paymentMethod: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Conta bancária"
                value={receivableForm.bankAccountId}
                onChange={(event) => setReceivableForm((current) => ({ ...current, bankAccountId: event.target.value }))}
              >
                <MenuItem value="">Sem vínculo</MenuItem>
                {bankAccounts.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id}>
                    {bank.name} • {bank.bankName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={receivableForm.notes}
                onChange={(event) => setReceivableForm((current) => ({ ...current, notes: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReceivableDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleSaveReceivable()} disabled={savingDialog}>
            {savingDialog ? 'Salvando...' : 'Salvar conta'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={receiveDialogOpen} onClose={closeReceiveDialog} fullWidth maxWidth="sm">
        <DialogTitle>Registrar recebimento</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Valor recebido"
              type="number"
              value={receiveForm.receivedAmount}
              onChange={(event) => setReceiveForm((current) => ({ ...current, receivedAmount: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Data/hora do recebimento"
              type="datetime-local"
              value={receiveForm.receivedAt}
              onChange={(event) => setReceiveForm((current) => ({ ...current, receivedAt: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Método"
              value={receiveForm.paymentMethod}
              onChange={(event) => setReceiveForm((current) => ({ ...current, paymentMethod: event.target.value }))}
            />
            <TextField
              select
              fullWidth
              label="Conta bancária"
              value={receiveForm.bankAccountId}
              onChange={(event) => setReceiveForm((current) => ({ ...current, bankAccountId: event.target.value }))}
            >
              <MenuItem value="">Sem vínculo</MenuItem>
              {bankAccounts.map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name} • {bank.bankName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Observações"
              multiline
              rows={3}
              value={receiveForm.notes}
              onChange={(event) => setReceiveForm((current) => ({ ...current, notes: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReceiveDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleReceiveReceivable()} disabled={savingDialog}>
            {savingDialog ? 'Salvando...' : 'Confirmar recebimento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={payableDialogOpen} onClose={closePayableDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingPayable ? 'Editar despesa' : 'Nova despesa'}</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Descrição"
                value={payableForm.description}
                onChange={(event) => setPayableForm((current) => ({ ...current, description: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Categoria"
                value={payableForm.expenseCategory}
                onChange={(event) => setPayableForm((current) => ({ ...current, expenseCategory: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Vencimento"
                value={payableForm.dueDate}
                onChange={(event) => setPayableForm((current) => ({ ...current, dueDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="number"
                label="Valor"
                value={payableForm.amount}
                onChange={(event) => setPayableForm((current) => ({ ...current, amount: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={payableForm.status}
                onChange={(event) => setPayableForm((current) => ({ ...current, status: event.target.value }))}
              >
                {payableStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Método"
                value={payableForm.paymentMethod}
                onChange={(event) => setPayableForm((current) => ({ ...current, paymentMethod: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Conta bancária"
                value={payableForm.bankAccountId}
                onChange={(event) => setPayableForm((current) => ({ ...current, bankAccountId: event.target.value }))}
              >
                <MenuItem value="">Sem vínculo</MenuItem>
                {bankAccounts.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id}>
                    {bank.name} • {bank.bankName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={payableForm.notes}
                onChange={(event) => setPayableForm((current) => ({ ...current, notes: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePayableDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleSavePayable()} disabled={savingDialog}>
            {savingDialog ? 'Salvando...' : 'Salvar despesa'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={payDialogOpen} onClose={closePayDialog} fullWidth maxWidth="sm">
        <DialogTitle>Registrar pagamento</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Valor pago"
              type="number"
              value={payForm.paidAmount}
              onChange={(event) => setPayForm((current) => ({ ...current, paidAmount: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Data/hora do pagamento"
              type="datetime-local"
              value={payForm.paidAt}
              onChange={(event) => setPayForm((current) => ({ ...current, paidAt: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Método"
              value={payForm.paymentMethod}
              onChange={(event) => setPayForm((current) => ({ ...current, paymentMethod: event.target.value }))}
            />
            <TextField
              select
              fullWidth
              label="Conta bancária"
              value={payForm.bankAccountId}
              onChange={(event) => setPayForm((current) => ({ ...current, bankAccountId: event.target.value }))}
            >
              <MenuItem value="">Sem vínculo</MenuItem>
              {bankAccounts.map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name} • {bank.bankName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Observações"
              multiline
              rows={3}
              value={payForm.notes}
              onChange={(event) => setPayForm((current) => ({ ...current, notes: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePayDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handlePayPayable()} disabled={savingDialog}>
            {savingDialog ? 'Salvando...' : 'Confirmar pagamento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bankDialogOpen} onClose={closeBankDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingBankAccount ? 'Editar conta bancária' : 'Nova conta bancária'}</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Nome da conta"
              value={bankForm.name}
              onChange={(event) => setBankForm((current) => ({ ...current, name: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Banco"
              value={bankForm.bankName}
              onChange={(event) => setBankForm((current) => ({ ...current, bankName: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Agência"
              value={bankForm.agency}
              onChange={(event) => setBankForm((current) => ({ ...current, agency: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Conta"
              value={bankForm.accountNumber}
              onChange={(event) => setBankForm((current) => ({ ...current, accountNumber: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Saldo inicial"
              type="number"
              value={bankForm.initialBalance}
              onChange={(event) => setBankForm((current) => ({ ...current, initialBalance: event.target.value }))}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={bankForm.active}
                  onChange={(event) => setBankForm((current) => ({ ...current, active: event.target.checked }))}
                />
              )}
              label="Conta ativa"
            />
            <TextField
              fullWidth
              label="Observações"
              multiline
              rows={3}
              value={bankForm.notes}
              onChange={(event) => setBankForm((current) => ({ ...current, notes: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBankDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleSaveBankAccount()} disabled={savingDialog}>
            {savingDialog ? 'Salvando...' : 'Salvar conta'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminErpWorkspace;
